import { Placement } from '@floating-ui/react'
import { Icon as PhosphorIcon } from '@phosphor-icons/react'
import React from 'react'
import { z } from 'zod'

import { BadgeColorKeys, BadgeVariantKeys } from '@/components/ui/badge'
import { TextInputLabelPosition } from '@/components/ui/text-input/text-input.types'

export type SelectSize = 'small' | 'medium'

export const SelectTypeEnum = z.enum(['single', 'multi', 'nested'])

export type SelectOptionValue = string | number | boolean

// Note: 'menu' is the design system's "Modal menu" (e.g. a table's "Choose columns to display:")
// - 12px label/row padding, 8px row gaps, 20px checkboxes and a left-aligned "Reset to default"
// row - instead of the regular dropdown list's 16px rows with 4px gaps.
export type SelectDropdownVariant = 'default' | 'menu'

export type SelectDropdownOptions = {
  label?: string
  width?: string
  variant?: SelectDropdownVariant
}

export interface SelectOption<V extends SelectOptionValue> {
  label: string
  value: V
  disabled?: boolean
  icon?: PhosphorIcon
  status?: string
  statusColor?: BadgeColorKeys
  // Note: when set, the option row renders `label` (+ `icon`, if provided) inside a
  // colored Badge pill instead of plain text - e.g. to mirror a colored status/reason
  // shown elsewhere (a table column, etc.) in the option list itself.
  badgeColor?: BadgeColorKeys
  badgeVariant?: BadgeVariantKeys
  // Note: a trailing ghost button on the row that runs its own action instead of toggling the
  // option - e.g. a table column's "Settings" in the column-visibility picker.
  action?: SelectOptionAction
}

export interface SelectOptionAction {
  label: string
  icon?: PhosphorIcon
  onClick: () => void
}

export interface SelectTreeOption<V extends SelectOptionValue> extends SelectOption<V> {
  children?: Array<SelectTreeOption<V>>
  defaultExpanded?: boolean
  hideMoveButton?: boolean
  /** Shown as a trailing quantity badge on this row, when provided. */
  count?: number
}

export type SelectDisplay = 'default' | 'badges'

export type CheckboxState = 'checked' | 'indeterminate' | 'unchecked'

interface SelectBaseProps<_V extends SelectOptionValue>
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  placeholder?: string
  loading?: boolean
  disabled?: boolean
  error?: string
  helperText?: string
  label?: string
  required?: boolean
  tooltip?: string
  name?: string
  size?: SelectSize
  searchable?: boolean
  // Note: placeholder for the dropdown's own search input (defaults to "Search...").
  // Distinct from `placeholder`, which is the closed trigger's 0-selected text.
  searchPlaceholder?: string
  // Note: Custom trigger element to open the select dropdown
  trigger?: React.ReactNode
  // Note: fixes the trigger box's own width (default: fills its parent, same as
  // `TextInput`). The dropdown isn't set separately - it auto-matches whatever
  // width the trigger renders at, unless `dropdownOptions.width` overrides that.
  width?: string
  dropdownOptions?: SelectDropdownOptions
  placement?: Placement
  onReset?: () => void
  // Note: Called when the search input value changes. When provided, local filtering is skipped
  // and the parent is responsible for updating `options` with server results.
  onSearchChange?: (query: string) => void
  icon?: PhosphorIcon
  display?: SelectDisplay
  /** Hides the trigger's caret while the field is disabled - for a value shown
   * read-only, so it doesn't look like a dropdown the user could still open. */
  hideCaretOnDisable?: boolean
  /** Shows the value as plain text (no border, padding or caret) and never opens -
   * the design's read-only state. Prefer this over `disabled` + `hideCaretOnDisable`. */
  readOnly?: boolean
  /** Mirrors `TextInput`: label above the field (default) or to its left. */
  labelPosition?: TextInputLabelPosition
  /** When provided, a clear (X) button shows after the caret while something is
   * selected. The consumer resets its own value - Select stays controlled. */
  onClear?: () => void
  /**
   * Mirrors `TextInput`: by default the footer row beneath the trigger renders
   * only when an `error` is present — otherwise no space is reserved. Set to
   * `true` to always reserve that row (so the control's height matches a
   * `TextInput` and an appearing error does not shift layout). Ignored when a
   * custom `trigger` is provided.
   */
  withReservedFooter?: boolean
  // Note: Selected options float to the top of the list by default. Set to `false` where the
  // option order itself carries meaning (e.g. a table's column picker mirroring column order).
  sortSelectedFirst?: boolean
}

export interface SelectSingleProps<V extends SelectOptionValue> extends SelectBaseProps<V> {
  type?: typeof SelectTypeEnum.enum.single
  options: Array<SelectOption<V>>
  value: V | null
  onChange: (value: V) => void
}

export interface SelectMultiProps<V extends SelectOptionValue> extends SelectBaseProps<V> {
  type: typeof SelectTypeEnum.enum.multi
  options: Array<SelectOption<V>>
  value: V[]
  onChange: (value: V[]) => void
}

interface SelectTreeBaseProps<V extends SelectOptionValue> extends SelectBaseProps<V> {
  type: typeof SelectTypeEnum.enum.nested
  options: Array<SelectTreeOption<V>>
}

export interface SelectTreeSingleProps<V extends SelectOptionValue> extends SelectTreeBaseProps<V> {
  // Note: default (single-pick) mode — a leaf is chosen directly, a parent via its
  // "Move" button; picking closes the dropdown. Unchanged from before checkboxes existed.
  checkbox?: false
  value: V | null
  onChange: (value: V) => void
}

export interface SelectTreeCheckboxProps<V extends SelectOptionValue>
  extends SelectTreeBaseProps<V> {
  // Note: checkbox (multi-pick) mode — every option renders a tri-state checkbox,
  // a parent's checkbox selects/clears its selectable descendant leaves, and the
  // dropdown stays open across picks (mirrors `multi`, but for a tree).
  checkbox: true
  value: V[]
  onChange: (value: V[]) => void
  // Note: trigger text shown when 2+ leaves are selected (e.g. "Multiple amenities/spaces").
  // Falls back to `placeholder` when omitted. Ignored when 0 or 1 leaves are selected —
  // those show `placeholder` and the single selected leaf's label, respectively.
  multipleSelectedLabel?: string
}

export type SelectTreeProps<V extends SelectOptionValue> =
  | SelectTreeSingleProps<V>
  | SelectTreeCheckboxProps<V>

export type SelectProps<V extends SelectOptionValue> =
  | SelectSingleProps<V>
  | SelectMultiProps<V>
  | SelectTreeProps<V>

// Note: Flattened option used internally for rendering tree structures
export interface FlattenedOption<V extends SelectOptionValue> {
  option: SelectTreeOption<V>
  depth: number
  hasChildren: boolean
}

export interface UseTreeOptionsParams<V extends SelectOptionValue> {
  filteredOptions: Array<SelectTreeOption<V>>
  originalOptions: Array<SelectTreeOption<V>>
  searchQuery: string
}
